import { useEffect, useState } from 'react';
import { api, type PlatformTenant } from '../api.ts';
import { message } from './Platform.tsx';

/**
 * Every Microsoft 365 tenant on the platform, and which client connected it.
 *
 * Its reason for existing is the duplicate check at the top. `entra_tenant_id`
 * is globally unique, so a second client trying to connect an estate somebody
 * else already has fails with a constraint violation that somebody then has to
 * explain — and the situation behind it is real: an MSP changes hands, or a
 * client moves provider. This is where that is visible before it becomes a
 * support conversation.
 */
export const PlatformEstate = () => {
  const [tenants, setTenants] = useState<PlatformTenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    void api
      .platformTenants()
      .then((response) => setTenants(response.tenants))
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  if (error !== null) return <p className="error-banner">{error}</p>;
  if (tenants === null) return <p className="note">Loading tenants…</p>;

  const needle = query.trim().toLowerCase();
  const shown =
    needle === ''
      ? tenants
      : tenants.filter(
          (tenant) =>
            tenant.displayName.toLowerCase().includes(needle) ||
            tenant.entraTenantId.toLowerCase().includes(needle) ||
            (tenant.accountName ?? '').toLowerCase().includes(needle),
        );

  return (
    <section className="pf-stack">
      <div className="card">
        <div className="card-head">
          <h2>Tenants across every client ({tenants.length})</h2>
        </div>
        <div className="card-body">
          <div className="field">
            <label htmlFor="pf-search">Search</label>
            <input
              id="pf-search"
              value={query}
              placeholder="tenant name, Entra tenant id, or client"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Entra tenant id</th>
                  <th>Client</th>
                  <th>Connection</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((tenant) => (
                  <tr key={tenant.id} className={tenant.archivedAt !== null ? 'pf-retired' : undefined}>
                    <td>
                      {tenant.displayName}
                      {tenant.archivedAt !== null && <span className="hint"> archived</span>}
                    </td>
                    <td className="mono">{tenant.entraTenantId}</td>
                    <td>{tenant.accountName ?? <em>unknown</em>}</td>
                    <td>{tenant.connectionState}</td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <span className="note">Nothing matches that.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};
